import { FanguardApiContractsClientCampaignUpdateRequestModel } from 'src/api/generated';

import { patch, post, remove } from '@/api/api';
import { siteQueries, SITE_URI } from '@/api/queryKeys';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import type { FanguardApiContractsClientSiteDetailModel } from '../../api/generated';
import { useMutationWithMessages } from './helpers';

interface CreateSiteProps {
  name: string;
  pathSegment: string;
}

const useGetDetails = () => {
  return useQuery({
    ...siteQueries.details,
  });
};

const useGetSites = () => {
  return useQuery({
    ...siteQueries.list,
  });
};

const useGetSite = (id?: string): UseQueryResult<FanguardApiContractsClientSiteDetailModel, Error> => {
  return useQuery({
    ...siteQueries.detail(id ?? ''),
    enabled: !!id,
  });
};

const useCreateSite = () => {
  return useMutationWithMessages((data: CreateSiteProps) => post({ uri: SITE_URI, data }), {
    action: 'create',
    entityType: 'site',
    queryKeysToInvalidate: [siteQueries.list._def],
  });
};

interface UpdateSiteProps {
  id: string;
  name?: string;
  pathSegment?: string;
  authenticationConnectionId?: string;
  requirePersonNames?: boolean;
}

const useUpdateSite = (id: string) => {
  return useMutationWithMessages((data: UpdateSiteProps) => patch({ uri: `${SITE_URI}/${data.id}`, data: data }), {
    action: 'update',
    entityType: 'site',
    queryKeysToInvalidate: [siteQueries.list._def, siteQueries.detail(id).queryKey],
  });
};

const useDeleteSite = (id?: string) => {
  return useMutationWithMessages(() => remove({ uri: `${SITE_URI}/${id}` }), {
    action: 'delete',
    entityType: 'site',
    queryKeysToInvalidate: [siteQueries.list._def],
  });
};

const useGetCampaign = (siteId?: string, campaignId?: string) => {
  return useQuery({
    ...siteQueries.campaign(siteId ?? '', campaignId ?? ''),
    enabled: !!siteId && !!campaignId,
  });
};

const useCreateCampaign = (siteId: string) => {
  return useMutationWithMessages(
    (data: { id: string; name: string }) =>
      post({ uri: `${SITE_URI}/${data?.id}/campaign`, data: { name: data?.name } }),
    {
      action: 'create',
      entityType: 'campaign',
      queryKeysToInvalidate: [siteQueries.detail(siteId).queryKey],
    },
  );
};

const useUpdateCampaign = (siteId: string, campaignId: string) => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientCampaignUpdateRequestModel>,
    Error,
    FanguardApiContractsClientCampaignUpdateRequestModel
  >((data) => patch({ uri: `${SITE_URI}/${data?.id}/campaign/${data?.campaignId}`, data }), {
    action: 'update',
    entityType: 'campaign',
    queryKeysToInvalidate: [
      siteQueries.list._def,
      siteQueries.campaign(siteId, campaignId).queryKey,
      siteQueries.detail(siteId).queryKey,
    ],
  });
};

const useDeleteCampaign = (siteId: string) => {
  return useMutationWithMessages(
    (data: { id: string; campaignId: string }) =>
      remove({ uri: `${SITE_URI}/${data?.id}/campaign/${data?.campaignId}` }),
    {
      action: 'delete',
      entityType: 'campaign',
      queryKeysToInvalidate: [siteQueries.detail(siteId).queryKey],
    },
  );
};

export {
  useCreateCampaign,
  useCreateSite,
  useDeleteCampaign,
  useDeleteSite,
  useGetCampaign,
  useGetDetails,
  useGetSite,
  useGetSites,
  useUpdateCampaign,
  useUpdateSite,
};
